import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowUpRight, Clock, Command, CornerDownLeft } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AutoCompleteGoogle from 'src/components/AutoCompleteGoogle/AutoCompleteGoogle';
import ButtonCmp from 'src/components/ButtonCmp';
import InputMaskCmp from 'src/components/InputMaskCmp';
import InputText from 'src/components/InputText/InputText';
import MapBox from 'src/components/MapBox/MapBox';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { createAddress, updateSingleAddress } from 'src/services/AddressBookServices';
import { getDuplicateAddressMessage } from 'src/utils/addressBookDuplicate';
import { formatTimeInput, getTimeMaskBlurHandler } from 'src/utils/CommonFunctions';
import * as Yup from 'yup';

import Address from '../AddressDetails/Address';

interface IAddress {
    address1: string;
    city: string;
    country: string;
    countryCode: string;
    fullAddress?: string;
    latitude?: string;
    longitude?: string;
    postal?: string;
    state?: string;
    stateCode?: string;
    streetNumber?: string;
    timezone?: string;
    additionalServiceIds?: number[];
}

interface IFormData {
    companyName: string;
    address: IAddress | null;
    businessHoursStartTime: string;
    businessHoursEndTime: string;
}

const toHHMM = (val?: string | null): string => (val ? String(val).slice(0, 5) : '');

interface EditShipperConsigneeFormProps {
    handleEditShipper: (isEdit?: boolean) => () => void;
    id?: number | null;
    handleSaveShipper: (data: IFormData) => void;
    isShipper?: boolean;
    isOpenFromQuoting?: boolean;
    initialCompanyName?: string;
    initialAddress?: IAddress | null;
}

const EditShipperConsigneeForm = ({
    handleEditShipper,
    id,
    handleSaveShipper,
    isShipper = true,
    isOpenFromQuoting = false,
    initialCompanyName = '',
    initialAddress = null,
}: EditShipperConsigneeFormProps) => {
    const { t } = useTranslation();
    // Get data from useAddressDetails hook (from basic.slice, address.slice, and map.slice)
    const { googleMapStatus, shippers, getShipper, consignees } = useAddressDetails();
    const [isAddressOpen, setIsAddressOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const timeFieldSchema = Yup.string()
        .nullable()
        .transform((val) => (val ? val : ''))
        .test('valid-time', t('ValidationMessages.Invalid_time_format_HH_MM'), (value) => {
            if (!value) return true;

            return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
        });

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                companyName: Yup.string().required('Shipper name is required'),
                businessHoursStartTime: timeFieldSchema.test('start-required-with-end', t('ValidationMessages.Business_Hours_Both_Required'), function (value) {
                    const { businessHoursEndTime } = this.parent;

                    if (businessHoursEndTime && !value) return false;

                    return true;
                }),
                businessHoursEndTime: timeFieldSchema
                    .test('end-required-with-start', t('ValidationMessages.Business_Hours_Both_Required'), function (value) {
                        const { businessHoursStartTime } = this.parent;

                        if (businessHoursStartTime && !value) return false;

                        return true;
                    })
                    .test('end-after-start', t('ValidationMessages.Business_Hours_End_After_Start'), function (value) {
                        const { businessHoursStartTime } = this.parent;
                        if (!businessHoursStartTime || !value) return true;

                        return value > businessHoursStartTime;
                    }),
                address: isOpenFromQuoting
                    ? Yup.object()
                          .shape({
                              fullAddress: Yup.string().required('Shipper address is required'),
                          })
                          .required('Shipper address is required')
                    : Yup.object()
                          .shape({
                              fullAddress: Yup.string().required('Shipper address is required'),
                              address1: Yup.string().required('Address1 is required'),
                              city: Yup.string().required('City is required'),
                              country: Yup.string().required('Country is required'),
                              countryCode: Yup.string().required('Country code is required'),
                              latitude: Yup.string().required('Latitude is required'),
                              longitude: Yup.string().required('Longitude is required'),
                              postal: Yup.string().required('Postal code is required'),
                              state: Yup.string().required('State is required'),
                              stateCode: Yup.string().required('State code is required'),
                              streetNumber: Yup.string().required('Street number is required'),
                          })
                          .required('Shipper address is required'),
            }),
        [isOpenFromQuoting]
    );

    const getDefaultAddress = () => {
        if (id) {
            // Try the loaded shippers list first; fall back to the prop so the
            // form is populated immediately even if shippers haven't loaded yet.
            const found = isShipper ? shippers.find((shipper: any) => shipper.id === id) : consignees.find((consignee: any) => consignee.id === id);

            return found ?? initialAddress ?? null;
        }

        return initialAddress ?? null;
    };

    const getDefaultCompanyName = () => {
        if (id) {
            const found = isShipper ? shippers.find((shipper: any) => shipper.id === id) : consignees.find((consignee: any) => consignee.id === id);

            return found?.companyName ?? initialCompanyName ?? '';
        }

        return initialCompanyName ?? '';
    };

    const {
        watch,
        control,
        setValue,
        setError,
        handleSubmit,
        clearErrors,
        formState: { errors },
    } = useForm<IFormData>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onSubmit',
        defaultValues: {
            address: initialAddress ?? getDefaultAddress(),
            companyName: getDefaultCompanyName(),
            businessHoursStartTime: id
                ? toHHMM(isShipper ? shippers.find((s: any) => s.id === id)?.businessHoursStartTime : consignees.find((c: any) => c.id === id)?.businessHoursStartTime)
                : '09:00',
            businessHoursEndTime: id ? toHHMM(isShipper ? shippers.find((s: any) => s.id === id)?.businessHoursEndTime : consignees.find((c: any) => c.id === id)?.businessHoursEndTime) : '15:00',
        },
    });
    const watchShipperAddress = watch('address');
    const watchShipperFullAddress = watch('address')?.fullAddress;
    const watchCompanyName = watch('companyName');
    const watchBusinessHoursStartTime = watch('businessHoursStartTime');
    const watchBusinessHoursEndTime = watch('businessHoursEndTime');

    const handleStartTimeChange = (e: any) => {
        setValue('businessHoursStartTime', formatTimeInput(e.target.value, watchBusinessHoursStartTime || ''));
        clearErrors('businessHoursStartTime');
    };

    const handleEndTimeChange = (e: any) => {
        setValue('businessHoursEndTime', formatTimeInput(e.target.value, watchBusinessHoursEndTime || ''));
        clearErrors('businessHoursEndTime');
    };

    const initialValuesRef = useRef<IFormData | null>(null);

    useEffect(() => {
        if (initialValuesRef.current === null) {
            const defaultAddr = getDefaultAddress();
            const defaultName = getDefaultCompanyName();
            const record = id ? (isShipper ? shippers.find((s: any) => s.id === id) : consignees.find((c: any) => c.id === id)) : null;

            initialValuesRef.current = {
                companyName: defaultName,
                address: defaultAddr ? JSON.parse(JSON.stringify(defaultAddr)) : null,
                businessHoursStartTime: record ? toHHMM(record?.businessHoursStartTime) : '09:00',
                businessHoursEndTime: record ? toHHMM(record?.businessHoursEndTime) : '15:00',
            };
        }
    }, [id, isShipper, shippers, consignees, initialCompanyName, initialAddress]);

    // Compare current values with initial values
    const hasFormChanged = useMemo(() => {
        if (initialValuesRef.current === null) {
            return false;
        }

        // Compare company name
        if (watchCompanyName !== initialValuesRef.current.companyName) {
            return true;
        }

        if ((watchBusinessHoursStartTime || '') !== (initialValuesRef.current.businessHoursStartTime || '')) {
            return true;
        }

        if ((watchBusinessHoursEndTime || '') !== (initialValuesRef.current.businessHoursEndTime || '')) {
            return true;
        }

        // Compare address
        const currentAddress = watchShipperAddress;
        const storedInitialAddress = initialValuesRef.current.address;

        // If both are null, no change
        if (!currentAddress && !storedInitialAddress) {
            return false;
        }

        // If one is null and the other is not, there's a change
        if (!currentAddress || !storedInitialAddress) {
            return true;
        }

        // Deep compare address objects
        const normalizeVal = (val: any): string => (val == null ? '' : String(val));
        const addressFields: (keyof IAddress)[] = ['fullAddress', 'address1', 'city', 'country', 'countryCode', 'latitude', 'longitude', 'postal', 'state', 'stateCode', 'streetNumber', 'timezone'];

        for (const field of addressFields) {
            if (field === 'latitude' || field === 'longitude') {
                const curr = parseFloat(normalizeVal(currentAddress[field]));
                const init = parseFloat(normalizeVal(storedInitialAddress[field]));

                if (isNaN(curr) !== isNaN(init)) return true;
                if (!isNaN(curr) && !isNaN(init) && Math.abs(curr - init) > 0.000001) return true;
                continue;
            }

            if (normalizeVal(currentAddress[field]) !== normalizeVal(storedInitialAddress[field])) {
                return true;
            }
        }

        // Compare additionalServiceIds arrays
        const currentServiceIds = currentAddress.additionalServiceIds?.sort().join(',') || '';
        const storedInitialServiceIds = storedInitialAddress.additionalServiceIds?.sort().join(',') || '';

        if (currentServiceIds !== storedInitialServiceIds) {
            return true;
        }

        return false;
    }, [watchCompanyName, watchShipperAddress, watchBusinessHoursStartTime, watchBusinessHoursEndTime]);

    const handleAddressInputChange = (value: string) => {
        if (value === '') {
            setValue('address', null);
            clearErrors('address');

            return;
        }

        // Clear all derived fields when user manually types/pastes so stale
        // city/state/country from a previously saved address cannot slip through validation.
        // The user must select a proper entry from the Google Places dropdown to repopulate them.
        setValue('address', {
            fullAddress: value,
            address1: '',
            city: '',
            country: '',
            countryCode: '',
            latitude: '',
            longitude: '',
            postal: '',
            state: '',
            stateCode: '',
            streetNumber: '',
            timezone: '',
            additionalServiceIds: watchShipperAddress?.additionalServiceIds ?? [],
        });
        clearErrors('address');
    };

    const handleAutocompleteChange = (value: any) => {
        if (!value) {
            if (!watchShipperFullAddress) {
                setValue('address', null);
            }

            return;
        }

        if (value.type === 'blur' || (value.target && value.type)) {
            if (!watchShipperFullAddress) {
                setValue('address', null);
            }

            return;
        }

        if (typeof value === 'object' && !value.target && (value.fullAddress === '' || (!value.fullAddress && !value.address1 && !value.city))) {
            setValue('address', null);

            return;
        }

        if (typeof value === 'object' && !value.target && value.fullAddress !== undefined) {
            const initialAddr = initialValuesRef.current?.address as any;

            if (value.fullAddress && initialAddr?.fullAddress && value.fullAddress === initialAddr.fullAddress) {
                setValue('address', initialValuesRef.current!.address);
                clearErrors('address');

                return;
            }

            setValue('address', {
                ...(watchShipperAddress || {}),
                fullAddress: value.fullAddress ?? '',
                address1: value.address1 ?? watchShipperAddress?.address1 ?? '',
                city: value.city ?? watchShipperAddress?.city ?? '',
                country: value.country ?? watchShipperAddress?.country ?? '',
                countryCode: value.countryCode ?? watchShipperAddress?.countryCode ?? '',
                latitude: value.latitude ?? watchShipperAddress?.latitude ?? '',
                longitude: value.longitude ?? watchShipperAddress?.longitude ?? '',
                postal: value.postal ?? watchShipperAddress?.postal ?? '',
                state: value.state ?? watchShipperAddress?.state ?? '',
                stateCode: value.stateCode ?? watchShipperAddress?.stateCode ?? '',
                streetNumber: value.streetNumber ?? watchShipperAddress?.streetNumber ?? '',
                timezone: value.timezone ?? watchShipperAddress?.timezone ?? '',
                additionalServiceIds: value.additionalServiceIds ?? watchShipperAddress?.additionalServiceIds ?? [],
            });
            clearErrors('address');
        }
    };
    const MapComponent = useMemo(
        () => (
            <MapBox
                draggable={false}
                address={watchShipperAddress && watchShipperAddress.fullAddress && watchShipperAddress.fullAddress !== '' ? watchShipperAddress : null}
                id="shipperMap"
                onReverseAddressFunc={handleAutocompleteChange}
                className="rounded-lg bg-white h-full border border-utilityGray200"
            />
        ),
        [watchShipperAddress, watchShipperFullAddress]
    );

    const handleSave = useCallback(
        async (data: IFormData) => {
            if (id) {
                setIsLoading(true);
                const payload = {
                    id: id,
                    ...data.address,
                    additionalServiceIds: data.address?.additionalServiceIds?.map((serviceId: number) => +serviceId),
                    type: isShipper ? 1 : 2,
                    companyName: data.companyName,
                    limitedAccessType: null,
                    addressType: isShipper ? 'SHIPPER' : 'CONSIGNEE',
                    contactName: data.companyName,
                    contactNumber: '',
                    latitude: data.address?.latitude ? String(data.address.latitude) : '',
                    longitude: data.address?.longitude ? String(data.address.longitude) : '',
                    businessHoursStartTime: data.businessHoursStartTime || null,
                    businessHoursEndTime: data.businessHoursEndTime || null,
                };

                updateSingleAddress(payload)
                    .then((result: any) => {
                        if (result) {
                            getShipper();
                            const newData = {
                                id: result.data.id,
                                companyName: result.data.companyName,
                                address: result.data.address,
                                timezone: result.data.timezone,
                                businessHoursStartTime: toHHMM(result.data.businessHoursStartTime),
                                businessHoursEndTime: toHHMM(result.data.businessHoursEndTime),
                            };
                            handleSaveShipper(newData);
                        }
                    })
                    .catch((error: any) => {
                        const duplicateMessage = getDuplicateAddressMessage(error, t('AddressBook.Duplicate_Address_Exists'));

                        if (duplicateMessage) {
                            // The address itself is fine — the customer already has this exact
                            // name + address on file, so flag the name and say so rather than
                            // claiming the address is missing. Shown on the field's alert icon,
                            // not in a toast (client, 2026-10-09).
                            setError('companyName', { type: 'server', message: duplicateMessage });

                            return;
                        }

                        if (error?.response?.data) {
                            setError('address', {
                                type: 'manual',
                                message: t('ValidationMessages.Shipper_address_is_required'),
                            });
                        }
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            } else {
                setIsLoading(true);
                const payload = {
                    ...data.address,
                    additionalServiceIds: data.address?.additionalServiceIds?.map((serviceId: number) => +serviceId),
                    type: isShipper ? 1 : 2,
                    companyName: data.companyName,
                    addressType: isShipper ? 'SHIPPER' : 'CONSIGNEE',
                    contactName: data.companyName,
                    contactNumber: '',
                    latitude: data.address?.latitude ? String(data.address.latitude) : '',
                    longitude: data.address?.longitude ? String(data.address.longitude) : '',
                    businessHoursStartTime: data.businessHoursStartTime || null,
                    businessHoursEndTime: data.businessHoursEndTime || null,
                };
                createAddress(payload)
                    .then((result: any) => {
                        if (result) {
                            getShipper();
                            const newData = {
                                id: result.data.id,
                                companyName: result.data.companyName,
                                address: result.data.address,
                                timezone: result.data.timezone,
                                businessHoursStartTime: toHHMM(result.data.businessHoursStartTime),
                                businessHoursEndTime: toHHMM(result.data.businessHoursEndTime),
                            };
                            handleSaveShipper(newData);
                        }
                    })
                    .catch((error: any) => {
                        const duplicateMessage = getDuplicateAddressMessage(error, t('AddressBook.Duplicate_Address_Exists'));

                        if (duplicateMessage) {
                            // The address itself is fine — the customer already has this exact
                            // name + address on file, so flag the name and say so rather than
                            // claiming the address is missing. Shown on the field's alert icon,
                            // not in a toast (client, 2026-10-09).
                            setError('companyName', { type: 'server', message: duplicateMessage });

                            return;
                        }

                        if (error?.response?.data) {
                            setError('address', {
                                type: 'manual',
                                message: t('ValidationMessages.Shipper_address_is_required'),
                            });
                        }
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }
        },
        [id, isShipper, getShipper, handleSaveShipper]
    );

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && (event.key === 'Enter' || event.key === 'Return')) {
                if (!hasFormChanged || isLoading) {
                    event.preventDefault();

                    return;
                }
                event.preventDefault();
                handleSubmit(handleSave)();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleSubmit, handleSave, hasFormChanged, isLoading]);

    return (
        <>
            <Controller
                name="companyName"
                control={control}
                render={({ field: { value }, fieldState: { error } }) => (
                    <InputText
                        label={t('QuotingHub.QuotingDashboard.Name')}
                        labelClassName="block mb-1.5"
                        required
                        inputName="companyName"
                        placeholder={isShipper ? t('QuotingHub.QuotingRequest.Enter_shipper_name') : t('QuotingHub.QuotingDashboard.Enter_consignee_name')}
                        parentClassName="w-full"
                        value={value}
                        onChangeFunc={(e) => {
                            setValue('companyName', e.target.value);
                            clearErrors('companyName');
                        }}
                        isError={!!error}
                        // Server-side rejections (the duplicate name+address guard) ride on the alert icon
                        // inside the field, on hover. Required-field errors stay border-only per the house
                        // rule, so the message is gated on the `server` error type rather than on any error.
                        errorText={error?.type === 'server' ? error.message : undefined}
                        isIconError={error?.type === 'server'}
                        maxLength={50}
                    />
                )}
            />

            <div className="flex-1 space-y-3">
                <div className="flex-1 flex gap-x-2">
                    {googleMapStatus && (
                        <Controller
                            name={`address.fullAddress`}
                            control={control}
                            render={({ fieldState: { error } }) => (
                                <AutoCompleteGoogle
                                    label={t('Common.Address')}
                                    labelClassName="block mb-1.5"
                                    required
                                    inputName={`fullAddress`}
                                    placeholder={t('QuotingHub.QuotingDashboard.Enter_address')}
                                    className={`form_control truncate`}
                                    mapParentClass="w-full"
                                    isClearable
                                    onAddressInputChange={handleAddressInputChange}
                                    onChangeFunc={handleAutocompleteChange}
                                    onBlur={handleAutocompleteChange}
                                    value={watchShipperFullAddress || ''}
                                    errorText={!!errors?.address}
                                    errorTextTooltip={error?.type === 'manualWithTooltip' ? error?.message : null}
                                    disabled={false}
                                />
                            )}
                        />
                    )}
                </div>

                <div>
                    <label className="form_label block mb-1.5">{t('Common.Business_Hours')}</label>
                    <div className="flex gap-x-3">
                        <Controller
                            name="businessHoursStartTime"
                            control={control}
                            render={({ field: { value }, fieldState: { error } }) => (
                                <InputMaskCmp
                                    inputName="businessHoursStartTime"
                                    parentClassName="flex-1"
                                    placeholder={t('Common.Start_Time')}
                                    value={value || ''}
                                    HandleChange={handleStartTimeChange}
                                    handleBlur={(e: any) => getTimeMaskBlurHandler(e, handleStartTimeChange)}
                                    mask="99:99"
                                    icon={
                                        <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                            <Clock className="h-4 w-4" />
                                        </div>
                                    }
                                    isError={!!error?.message}
                                    errorText={error?.message}
                                />
                            )}
                        />
                        <Controller
                            name="businessHoursEndTime"
                            control={control}
                            render={({ field: { value }, fieldState: { error } }) => (
                                <InputMaskCmp
                                    inputName="businessHoursEndTime"
                                    parentClassName="flex-1"
                                    placeholder={t('Common.End_Time')}
                                    value={value || ''}
                                    HandleChange={handleEndTimeChange}
                                    handleBlur={(e: any) => getTimeMaskBlurHandler(e, handleEndTimeChange)}
                                    mask="99:99"
                                    icon={
                                        <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                            <Clock className="h-4 w-4" />
                                        </div>
                                    }
                                    isError={!!error?.message}
                                    errorText={error?.message}
                                />
                            )}
                        />
                    </div>
                </div>

                {!isOpenFromQuoting && <div className="rounded-lg shadow-[0px_4px_8px_-2px_rgba(16,_24,_40,_0.10),_0px_2px_4px_-2px_rgba(16,_24,_40,_0.06)] overflow-hidden">{MapComponent}</div>}
                <div className="flex justify-between items-center gap-x-2">
                    <ButtonCmp
                        type="button"
                        className="btn-outline-primary"
                        icon={<ArrowUpRight className="w-4 h-4" />}
                        onClick={() => {
                            setIsAddressOpen(true);
                        }}
                        disabled={false}
                    >
                        Open
                    </ButtonCmp>

                    <div className="flex items-center gap-x-2">
                        <ButtonCmp className="btn-link-secondary" size="none" onClick={handleEditShipper(false)}>
                            {t('Common.Cancel')}
                        </ButtonCmp>
                        <ButtonCmp
                            className="btn_primary"
                            iconSide="right"
                            onClick={handleSubmit(handleSave)}
                            loading={isLoading}
                            disabled={isLoading || (!id ? false : !hasFormChanged)}
                            icon={
                                <>
                                    <Command className="w-4 h-4" />
                                    <span>+</span>
                                    <CornerDownLeft className="w-4 h-4" />
                                </>
                            }
                        >
                            {t('Common.Save')}
                        </ButtonCmp>
                    </div>
                </div>
            </div>

            <Address
                isOpen={isAddressOpen}
                mode={watchShipperAddress ? 'shipperAddress' : 'addressCreate'}
                handleClose={() => {
                    setIsAddressOpen(false);
                }}
                setAddressData={(addressData: any) => {
                    setValue('address', addressData);
                    clearErrors('address');
                }}
                addressData={watchShipperAddress}
                status={googleMapStatus}
                alertMessageShow={isOpenFromQuoting ? false : true}
            />
        </>
    );
};
export default EditShipperConsigneeForm;
